/* eslint-disable @nx/enforce-module-boundaries */
import { AddIcon } from '@chakra-ui/icons';
import {
  BackArrow,
  ChevronDown,
  Copy,
  DeleteIcon,
  Move,
} from '@mybridge/icons';
import { AddFolderIcon } from '@mybridge/icons/AddFolder';
import { GridIcon } from '@mybridge/icons/Grid';
import { ListIcon } from '@mybridge/icons/List';
import { ShareFolderIcon } from '@mybridge/icons/ShareFolder';
import { UploadIcon } from '@mybridge/icons/Upload';
import {
  Button,
  ButtonGroup,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Stack,
  Text,
  useBreakpointValue,
} from '@mybridge/ui';
import { forwardRef, useContext, useRef } from 'react';
import { DiskBreadCrumbBar } from '../../breadcrumb-bar';
import { DiskContext } from '../../context';
import { DiskSearchToggle } from '../../search-toggle';
import { isMobile } from 'react-device-detect';
import { useTranslator } from '@mybridge/ui/custom-hooks/useTranslation';
import { useDirection } from '@mybridge/ui/custom-hooks/useDirection';
import { useSelector } from 'react-redux';

export const MyDiskListingHeader = forwardRef(({ ...props }, ref) => {
  const {
    createFolderDisc,
    uploadDisc,
    selectedDir,
    setView,
    view,
    filters,
    setFilters,
    setCreateSharedFolder,
    selectedFiles,
    multiDeleteDisc,
    multiCopyDisc,
    multiMoveDisc,
    selectedMainDirType,
    selectedMainDir,
    mobileFilePanelDisc,
    teamDiskMemberInfo,
  } = useContext(DiskContext);
  const { userProfileInfo } = useSelector((state) => state.userProfile);
  

  const { t } = useTranslator();

  const onViewChange = (view_) => setView(view_);
  const tid = useRef(-1);
  const onSearch = (name) => {
    clearTimeout(tid.current);
    tid.current = setTimeout(() => {
      setFilters({ ...(filters ?? {}), name });
    }, 300);
  };

  // Responsive styles for button/icon sizes
  const buttonSize = useBreakpointValue({ base: 'xs', md: 'sm' });
  const iconSize = useBreakpointValue({ base: '16px', md: '18px' });
  const iconColor = (currentView) => (view === currentView ? '#3D5A80' : '#5B5B5B');
  const direction = useDirection();

  return (
    <Stack pos="relative" zIndex="2" bg="white" {...props}>
      {selectedMainDir?.id && selectedFiles?.length && !isMobile ? (
        <HStack>
          <Text>{t('myDisk.withSelected') || "With selected"}: </Text>
          <Button borderRadius="full" onClick={multiDeleteDisc?.onOpen} colorScheme="red">
            {t('buttons.delete') || "Delete"}
          </Button>
          <Button borderRadius="full" onClick={multiCopyDisc?.onOpen} variant="secondary">
            {t('myDisk.copy') || "Copy"}
          </Button>
          <Button borderRadius="full" onClick={multiMoveDisc?.onOpen} variant="secondary">
            {t('myDisk.move') || "Move"}
          </Button>
        </HStack>
      ) : (
        <Stack direction={{ base: 'row', md: 'row' }} justifyContent="space-between" bg="white" flexWrap='wrap' px={[2,'']}>
          <HStack spacing={[1,2]}>
            {isMobile && (
              <BackArrow cursor="pointer" onClick={() => mobileFilePanelDisc?.onClose()} />
            )}
            <DiskBreadCrumbBar
              nameGetter={(item) =>
                item?.team_info?.name?.length 
                ? (item?.isMainDir ? item.team_info.name: item.display_name )
                : item.name
              }
            />
          </HStack>
          {selectedDir?.id && 
            (selectedDir?.team_info?.id 
              ? (teamDiskMemberInfo === "W" || userProfileInfo?.id === selectedDir?.owner?.id) 
              : (selectedDir?.owner?.id && selectedDir?.owner?.id !== userProfileInfo?.id 
                  ? selectedDir?.access_type === 2 
                  : true)) && 
            !(selectedDir?.isMainDir && selectedMainDirType === 'team-disk') && (
            <HStack spacing={2}>
              <ButtonGroup isAttached>
                <Button borderRadius="30px" onClick={uploadDisc?.onOpen} leftIcon={<UploadIcon />} variant="primary" size={buttonSize}>
                  {t('myDisk.upload') || "Upload"}
                </Button>
              </ButtonGroup>
              <ButtonGroup isAttached>
                <Button
                  sx={{
                    ...(direction === 'rtl') ? {borderTopRightRadius: '30px !important', borderBottomRightRadius: '30px !important', borderTopLeftRadius: selectedMainDir?.type !== "TD" ? '0px' : '30px', borderBottomLeftRadius: selectedMainDir?.type !== "TD" ? '0px' : '30px'} : {borderTopRightRadius: selectedMainDir?.type !== "TD" ? '0px' : '30px', borderBottomRightRadius: selectedMainDir?.type !== "TD" ? '0px' : '30px', borderTopLeftRadius: '30px', borderBottomLeftRadius: '30px'}
                  }}
                  onClick={createFolderDisc?.onOpen}
                  leftIcon={<AddIcon />}
                  variant="secondary"
                  size={buttonSize}
                  color="#3D5A80"
                >
                  {t('myDisk.create') || "Create"}
                </Button>
                {selectedMainDir?.type !== "TD" && (
                  <Menu>
                    {/* border-top-right-radius: 0;
                    border-bottom-right-radius: 0; */}
                    <MenuButton {...(direction === 'rtl') ? {borderTopRightRadius: '0px', borderBottomRightRadius: '0px', borderTopLeftRadius: '30px', borderBottomLeftRadius: '30px'} : {borderTopRightRadius: '30px', borderBottomRightRadius: '30px', borderTopLeftRadius: '0px', borderBottomLeftRadius: '0px'}} as={Button} variant="secondary" size={buttonSize} padding="0">
                      <ChevronDown color="#3D5A80" />
                    </MenuButton>
                    <MenuList>
                      <MenuItem onClick={createFolderDisc?.onOpen} icon={<AddFolderIcon />}>
                        {t('myDisk.folder') || "Folder"}
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          setCreateSharedFolder(true);
                          createFolderDisc?.onOpen();
                        }}
                        icon={<ShareFolderIcon />}
                      >
                        {t('myDisk.sharedFolder') || "Shared Folder"}
                      </MenuItem>
                    </MenuList>
                  </Menu>
                )}
              </ButtonGroup>

              {isMobile && selectedFiles?.length ? (
                <HStack>
                  <IconButton
                    size={buttonSize}
                    icon={<DeleteIcon color="white" width={iconSize} height={iconSize} />}
                    colorScheme="red"
                    onClick={multiDeleteDisc?.onOpen}
                  />
                  <IconButton
                    size={buttonSize}
                    icon={<Copy width={iconSize} height={iconSize} />}
                    onClick={multiCopyDisc?.onOpen}
                  />
                  <IconButton
                    size={buttonSize}
                    icon={<Move width={iconSize} height={iconSize} />}
                    onClick={multiMoveDisc?.onOpen}
                  />
                </HStack>
              ) : null}
            </HStack>
          )}

          <HStack justifyContent="flex-end" spacing={2}>
            <DiskSearchToggle value={filters?.name} onChange={onSearch} />
            <ButtonGroup isAttached>
              <IconButton
                isActive={view === 'grid'}
                onClick={() => onViewChange('grid')}
                size={buttonSize}
              >
                <GridIcon width={iconSize} height={iconSize} view={view} color={iconColor('grid')} />
              </IconButton>
              <IconButton
                isActive={view === 'list'}
                onClick={() => onViewChange('list')}
                size={buttonSize}
              >
                <ListIcon width={iconSize} height={iconSize} view={view} color={iconColor('list')} />
              </IconButton>
            </ButtonGroup>
          </HStack>
        </Stack>
      )}
    </Stack>
  );
});
